<template>
  <div
    class="image-upload"
    @drop="handleFileDrop"
    @dragenter="handleDragEnter"
    @dragleave="handleDragLeave"
    @dragover="handleDragOver"
  >
    <input
      data-private
      ref="inputFile"
      class="d-none"
      type="file"
      accept="image/*"
      name="image"
      :id="id"
      :disabled="disabled"
      :multiple="allowMultipleSelect"
      @change="handleInputFileChange"
    />

    <slot :selectFile="handleSelectFile" :isDraggingFile="isDraggingFile" />
  </div>
</template>

<script>
import uuid from 'uuid/v4'
import _ from 'lodash'

import mixin from '@/mixins/mixin.vue'

export default {
  mixins: [mixin],

  props: {
    id: {
      type: String,
      default: uuid(),
    },

    disabled: Boolean,

    allowMultipleSelect: Boolean,
  },

  data() {
    return {
      isDraggingFile: false,
    }
  },

  methods: {
    handleFileDrop(event) {
      event.preventDefault()
      event.dataTransfer.effectAllowed = 'none'

      if (this.disabled) return

      const files = Array.from(event?.dataTransfer?.files ?? [])

      this.isDraggingFile = false
      this.handleFilesSelected(files)
    },

    handleDragEnter(event) {
      event.preventDefault()
      event.dataTransfer.effectAllowed = 'none'

      if (this.disabled || this.isDraggingFile) return
      this.isDraggingFile = true
    },

    handleDragLeave() {
      if (this.disabled || !this.isDraggingFile) return
      this.isDraggingFile = false
    },

    handleDragOver(event) {
      event.preventDefault()
      event.dataTransfer.effectAllowed = 'none'
      if (this.disabled || this.isDraggingFile) return
      this.isDraggingFile = true
    },

    handleSelectFile() {
      if (this.disabled) return

      this.$refs.inputFile.click()
    },

    handleInputFileChange(event) {
      const files = Array.from(
        event?.target?.files ?? event?.dataTransfer?.files ?? []
      )

      this.$refs.inputFile.value = ''

      this.handleFilesSelected(files)
    },

    async handleFilesSelected(files) {
      if (_.isEmpty(files)) return

      try {
        this.validateFiles(files)

        const promises = files.map(file => this.fileToBase64(file))
        const base64Files = await Promise.all(promises)

        this.$emit('selected', base64Files)
      } catch (error) {
        this.showError(error?.message)
      }
    },

    validateFiles(files) {
      files.map(file => {
        const fileType = file?.type

        if (!this.$constants.images.acceptedMimeTypes.includes(fileType)) {
          throw new Error(
            this.$t('media.only_images_error', {
              supportedExtensions: this.$constants.images.acceptedExtensions.join(
                ', '
              ),
            })
          )
        }

        const fileSize = file?.size
        if (fileSize > this.$constants.images.maxFileSizeInBytes) {
          throw new Error(
            this.$t('media.file_size_error', {
              fileName: file?.name ?? '',
              maxSize: this.$constants.images.maxFileSizeInMB,
            })
          )
        }
      })
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = error => reject(error)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.image-upload {
  width: 100%;
  height: 100%;
}
</style>
